<template>
  <div class="space-y-6">
    <!-- Existing triggers -->
    <div v-if="triggers.length > 0" class="space-y-4">
      <h3 class="text-sm font-semibold text-gray-700 uppercase tracking-wider">Active Triggers</h3>
      <div v-for="(trigger, idx) in triggers" :key="idx" class="bg-white shadow rounded-lg p-4">
        <div class="flex items-start justify-between">
          <div class="space-y-1">
            <div v-if="trigger.triggerType === 'call_completed'" class="text-sm font-medium text-gray-900">
              Call Completed
            </div>
            <div v-else class="text-sm font-medium text-gray-900">
              {{ trigger.pipelineName || trigger.pipelineId }}
              <span class="text-gray-400 mx-1">&rarr;</span>
              {{ trigger.stageName || trigger.stageId }}
            </div>
            <div class="text-sm text-gray-600">Template: {{ templateName(trigger.templateId) }}</div>
            <div class="flex flex-wrap gap-2 mt-2">
              <span v-if="trigger.writebackNote" class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">Note</span>
              <span v-if="trigger.writebackTask" class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">Task{{ trigger.taskTitle ? ': ' + trigger.taskTitle : '' }}</span>
              <span v-if="Object.keys(trigger.writebackCustomFields || {}).length > 0" class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                {{ Object.keys(trigger.writebackCustomFields).length }} field mapping(s)
              </span>
              <span v-if="trigger.moveToStageId" class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                Move &rarr; {{ trigger.moveToStageName || trigger.moveToStageId }}
              </span>
            </div>
          </div>
          <button @click="removeTrigger(idx)" class="text-red-500 hover:text-red-700 text-sm font-medium">Remove</button>
        </div>
      </div>
    </div>

    <div v-else class="text-center py-8 text-gray-400">
      No triggers configured. Add one below to get started.
    </div>

    <!-- Add trigger form -->
    <div class="bg-white shadow rounded-lg p-6">
      <h3 class="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">Add Trigger</h3>

      <!-- Trigger type -->
      <div class="flex gap-4 mb-4">
        <label class="flex items-center gap-2 text-sm cursor-pointer">
          <input type="radio" v-model="newTrigger.triggerType" value="pipeline" class="text-blue-600 focus:ring-blue-500" />
          <span>Pipeline Stage Change</span>
        </label>
        <label class="flex items-center gap-2 text-sm cursor-pointer">
          <input type="radio" v-model="newTrigger.triggerType" value="call_completed" class="text-blue-600 focus:ring-blue-500" />
          <span>Call Completed</span>
        </label>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <!-- Pipeline (pipeline triggers only) -->
        <div v-if="newTrigger.triggerType === 'pipeline'">
          <label class="block text-sm font-medium text-gray-700 mb-1">Pipeline</label>
          <select v-model="newTrigger.pipelineId" @change="onPipelineChange" class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">-- Select Pipeline --</option>
            <option v-for="p in pipelines" :key="p.id" :value="p.id">{{ p.name }}</option>
          </select>
        </div>

        <!-- Stage (pipeline triggers only) -->
        <div v-if="newTrigger.triggerType === 'pipeline'">
          <label class="block text-sm font-medium text-gray-700 mb-1">Stage</label>
          <select v-model="newTrigger.stageId" @change="onStageChange" class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" :disabled="!newTrigger.pipelineId">
            <option value="">-- Select Stage --</option>
            <option v-for="s in stages" :key="s.id" :value="s.id">{{ s.name }}</option>
          </select>
        </div>

        <!-- Template -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Template</label>
          <select v-model="newTrigger.templateId" class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">-- Select Template --</option>
            <option v-for="t in templates" :key="t.id" :value="t.id">{{ t.name }}</option>
          </select>
        </div>
      </div>

      <!-- Writeback options -->
      <div class="space-y-3 mb-4">
        <label class="flex items-center space-x-2 text-sm">
          <input type="checkbox" v-model="newTrigger.writebackNote" class="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
          <span>Write contact note on submission</span>
        </label>

        <div>
          <label class="flex items-center space-x-2 text-sm">
            <input type="checkbox" v-model="newTrigger.writebackTask" class="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
            <span>Create follow-up task</span>
          </label>
          <input
            v-if="newTrigger.writebackTask"
            v-model="newTrigger.taskTitle"
            type="text"
            placeholder="Task title (optional)"
            class="mt-2 ml-6 border border-gray-300 rounded px-3 py-1.5 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <!-- Move to stage (pipeline triggers only) -->
        <div v-if="newTrigger.triggerType === 'pipeline'">
          <label class="block text-sm font-medium text-gray-700 mb-1">Move to Stage on Submission</label>
          <select v-model="newTrigger.moveToStageId" @change="onMoveToStageChange" class="w-64 border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" :disabled="!newTrigger.pipelineId">
            <option value="">-- Don't move --</option>
            <option v-for="s in stages" :key="s.id" :value="s.id">{{ s.name }}</option>
          </select>
        </div>

        <!-- Custom field mappings -->
        <div>
          <div class="text-sm font-medium text-gray-700 mb-2">Custom Field Mappings</div>
          <div v-if="selectedTemplateFields.length === 0" class="text-sm text-gray-400">Select a template to configure field mappings</div>
          <div v-else class="space-y-2">
            <div v-for="field in selectedTemplateFields" :key="field.key" class="flex items-center gap-3">
              <span class="text-sm text-gray-600 w-40 shrink-0">{{ field.label }}</span>
              <span class="text-gray-400">&rarr;</span>
              <select
                :value="newTrigger.writebackCustomFields[field.key] || ''"
                @change="setFieldMapping(field.key, $event.target.value)"
                class="flex-1 border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">-- None --</option>
                <option v-for="cf in customFields" :key="cf.id" :value="cf.id">{{ cf.name }}</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <button
        @click="addTrigger"
        :disabled="!canAdd"
        class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >Add Trigger</button>
    </div>

    <!-- Save -->
    <div class="flex items-center justify-between">
      <div v-if="saveMsg" :class="saveError ? 'text-red-600' : 'text-green-600'" class="text-sm">{{ saveMsg }}</div>
      <div v-else></div>
      <button
        @click="save"
        :disabled="saving"
        class="px-6 py-2 text-sm font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50"
      >
        {{ saving ? 'Saving...' : 'Save Settings' }}
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SettingsPanel',
  props: {
    locationId: { type: String, required: true },
    companyId: { type: String, default: null },
    templates: { type: Array, required: true }
  },
  data() {
    return {
      triggers: [],
      pipelines: [],
      customFields: [],
      saving: false,
      saveMsg: null,
      saveError: false,
      newTrigger: this.emptyTrigger()
    }
  },
  computed: {
    stages() {
      const p = this.pipelines.find(p => p.id === this.newTrigger.pipelineId)
      return p?.stages || []
    },
    selectedTemplateFields() {
      const t = this.templates.find(t => t.id === this.newTrigger.templateId)
      return t?.fields || []
    },
    canAdd() {
      if (this.newTrigger.triggerType === 'call_completed') {
        return !!this.newTrigger.templateId
      }
      return this.newTrigger.pipelineId && this.newTrigger.stageId && this.newTrigger.templateId
    }
  },
  async mounted() {
    const params = new URLSearchParams({ locationId: this.locationId })
    if (this.companyId) params.set('companyId', this.companyId)

    const [settingsRes, pipelinesRes, cfRes] = await Promise.all([
      fetch(`/opsflow/settings?${params}`).then(r => r.json()).catch(() => ({ triggers: [] })),
      fetch(`/opsflow/pipelines?${params}`).then(r => r.json()).catch(() => ({ pipelines: [] })),
      fetch(`/opsflow/custom-fields?${params}`).then(r => r.json()).catch(() => ({ customFields: [] }))
    ])

    this.triggers = settingsRes.triggers || []
    this.pipelines = pipelinesRes.pipelines || []
    this.customFields = cfRes.customFields || []
  },
  methods: {
    emptyTrigger() {
      return {
        triggerType: 'pipeline',
        pipelineId: '',
        pipelineName: '',
        stageId: '',
        stageName: '',
        templateId: '',
        writebackNote: false,
        writebackCustomFields: {},
        writebackTask: false,
        taskTitle: '',
        moveToStageId: '',
        moveToStageName: ''
      }
    },
    onPipelineChange() {
      const p = this.pipelines.find(p => p.id === this.newTrigger.pipelineId)
      this.newTrigger.pipelineName = p?.name || ''
      this.newTrigger.stageId = ''
      this.newTrigger.stageName = ''
    },
    onStageChange() {
      const s = this.stages.find(s => s.id === this.newTrigger.stageId)
      this.newTrigger.stageName = s?.name || ''
    },
    onMoveToStageChange() {
      const s = this.stages.find(s => s.id === this.newTrigger.moveToStageId)
      this.newTrigger.moveToStageName = s?.name || ''
    },
    setFieldMapping(fieldKey, ghlFieldId) {
      const mappings = { ...this.newTrigger.writebackCustomFields }
      if (ghlFieldId) {
        mappings[fieldKey] = ghlFieldId
      } else {
        delete mappings[fieldKey]
      }
      this.newTrigger.writebackCustomFields = mappings
    },
    addTrigger() {
      if (!this.canAdd) return
      this.triggers.push({ ...this.newTrigger, writebackCustomFields: { ...this.newTrigger.writebackCustomFields } })
      this.newTrigger = this.emptyTrigger()
    },
    removeTrigger(idx) {
      this.triggers.splice(idx, 1)
    },
    async save() {
      this.saving = true
      this.saveMsg = null
      this.saveError = false
      try {
        const res = await fetch('/opsflow/settings', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ locationId: this.locationId, companyId: this.companyId, triggers: this.triggers })
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data.error || 'Save failed')
        this.saveMsg = 'Settings saved'
      } catch (e) {
        this.saveMsg = e.message
        this.saveError = true
      } finally {
        this.saving = false
      }
    },
    templateName(id) {
      const t = this.templates.find(t => t.id === id)
      return t ? t.name : id
    }
  }
}
</script>
